/**
 * Other (Specify) field behaviour.
 *
 * - The "Other (specify)" checkbox toggles its free-text rows; unchecking
 *   clears them so nothing is submitted.
 * - The add button clones the row template and focuses the new input.
 * - Removing the last row is equivalent to unchecking — it collapses the
 *   wrapper rather than leaving an empty, input-less box.
 *
 * This mirrors the Other treatment in the Areas of Focus field, but is a
 * separate implementation on purpose (DRMB-989 is launch-critical). There is no
 * parent/child expansion and no select-all here — this field is only ever the
 * write-in block, and every instance is scoped to its own `.js-other-field`
 * root so several can share a page.
 */

const dispatchChange = (input: HTMLInputElement): void => {
  input.dispatchEvent(new Event("change", { bubbles: true }));
};

const attach = (field: Element): void => {
  const toggle = field.querySelector<HTMLInputElement>(
    ".js-other-field-toggle"
  );
  const wrapper = field.querySelector<HTMLElement>(
    ".other-field__rows-wrapper"
  );
  const rows = field.querySelector<HTMLElement>(".js-other-field-rows");
  const template = field.querySelector<HTMLTemplateElement>(
    ".js-other-field-template"
  );
  const addButton = field.querySelector<HTMLElement>(".js-other-field-add");
  if (!toggle || !wrapper || !rows || !template || !addButton) {
    return;
  }

  toggle.addEventListener("change", () => {
    wrapper.classList.toggle("hidden", !toggle.checked);
    toggle.setAttribute("aria-expanded", String(toggle.checked));

    if (!toggle.checked) {
      // Unchecking clears the entries so they aren't submitted.
      rows
        .querySelectorAll<HTMLInputElement>("input")
        .forEach((input) => (input.value = ""));
    } else if (!rows.querySelector(".other-field__row")) {
      // Re-checking with no rows left from a prior delete-all should still
      // leave one to type into. Reached by hand only: deleting the last row
      // unchecks the toggle, so the next click arrives with zero rows.
      rows.append(template.content.cloneNode(true));
    }
  });

  addButton.addEventListener("click", () => {
    rows.append(template.content.cloneNode(true));
    rows
      .querySelector<HTMLInputElement>(".other-field__row:last-child input")
      ?.focus();
  });

  rows.addEventListener("click", (event) => {
    const removeButton = (event.target as HTMLElement).closest(
      ".js-other-field-remove"
    );
    if (!removeButton) {
      return;
    }

    removeButton.closest(".other-field__row")?.remove();

    if (!rows.querySelector(".other-field__row")) {
      toggle.checked = false;
      dispatchChange(toggle);
    }
  });
};

const initialize = (): void =>
  document.querySelectorAll(".js-other-field").forEach((field) => {
    if ((field as HTMLElement).dataset.initialized) {
      return;
    }
    (field as HTMLElement).dataset.initialized = "true";

    attach(field);
  });

initialize();

// The member portal swaps form pages via HTMX, so without this a field on a
// later page arrives inert.
window.addEventListener("htmx:afterSwap", initialize);

export default initialize;
